import { ChevronRightIcon } from '@heroicons/react/24/outline';

export type TabbedFormActionsProps = {
  onCancel: () => void;
  tabCount: number;
  selectedTab: number;
  onTabChange: (index: number) => void;
  canSubmit: boolean;
  submitLabel: string;
  loading?: boolean;
  loadingLabel?: string;
  disabledReason?: string;
  cancelDisabled?: boolean;
};

/**
 * Shared footer for multi-tab create/edit dialogs: Cancel, Next (until last tab),
 * and a Save/Submit that stays disabled until mandatory fields are complete.
 */
export default function TabbedFormActions({
  onCancel,
  tabCount,
  selectedTab,
  onTabChange,
  canSubmit,
  submitLabel,
  loading = false,
  loadingLabel,
  disabledReason = 'Fill all required fields before saving',
  cancelDisabled = false,
}: TabbedFormActionsProps) {
  const isLastTab = selectedTab >= tabCount - 1;
  const submitDisabled = loading || !canSubmit;

  return (
    <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-4">
      <button
        type="button"
        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 disabled:opacity-50"
        onClick={onCancel}
        disabled={cancelDisabled || loading}
      >
        Cancel
      </button>

      {!isLastTab && (
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-cyan-700 shadow-sm ring-1 ring-inset ring-cyan-200 hover:bg-cyan-50 disabled:opacity-50"
          onClick={() => onTabChange(Math.min(selectedTab + 1, tabCount - 1))}
          disabled={loading}
        >
          Next
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      <button
        type="submit"
        disabled={submitDisabled}
        title={submitDisabled && !loading ? disabledReason : undefined}
        className="btn btn-primary text-sm shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? loadingLabel || 'Saving...' : submitLabel}
      </button>
    </div>
  );
}

export function isNonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

export function isValidPositiveNumber(value: unknown): boolean {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0;
  if (typeof value !== 'string' || !value.trim()) return false;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0;
}
